import { Box, VStack, useColorModeValue } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import CDS from "design-system";
import { format, startOfDay, endOfDay, subDays, parseISO } from "date-fns";
import { MdBugReport, MdCalendarToday, MdPerson, MdToday } from "react-icons/md";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ActiveFilter, SortOption } from "components/ControlBar/types";
import DateFilter from "components/Filters/DateFilter";
import FilterButton from "components/Button/FilterButton";
import TableFilterSelect from "components/Select/TableFilterSelect";
import { useNewHiresListQuery } from "features/NewHireJourney/hooks";
import { useCurrentUserQuery, useUserListQuery } from "features/User/hooks";
import { NewHireJourney } from "models/v2/newHireJourney";
import { PlatformEventFilters, EVENT_TYPE_PREFIXES, EventTypePrefix, EventName } from "../types";

const EVENT_TYPE_LABELS: Record<EventTypePrefix, string> = {
  email: "Email Events",
  message: "Slack Messages",
  task: "Task Events",
  meeting: "Meeting Events",
  new_hire_journey: "Journey Events",
  issue: "Issues",
  form: "Forms",
  nudge: "Nudges",
};

// Map event type prefixes to their specific event names
const EVENT_NAMES_BY_TYPE: Record<EventTypePrefix, EventName[]> = {
  email: ["email.sent", "email.failed", "email.skipped"],
  message: ["message.sent"],
  task: [
    "task.assigned",
    "task.assignee.changed",
    "task.completed",
    "task.due_date.changed",
    "task.notification.sent",
    "task.reminder.sent",
    "task.skipped",
  ],
  meeting: [
    "meeting.scheduled",
    "meeting.cancelled",
    "meeting.completed",
    "meeting.declined",
    "meeting.no_response",
    "meeting.rescheduled",
  ],
  new_hire_journey: [
    "new_hire_journey.created",
    "new_hire_journey.activated",
    "new_hire_journey.buddy.assigned",
    "new_hire_journey.buddy.changed",
    "new_hire_journey.manager.assigned",
    "new_hire_journey.manager.changed",
    "new_hire_journey.people_team_contact.assigned",
    "new_hire_journey.people_team_contact.changed",
    "new_hire_journey.start_date.changed",
  ],
  issue: ["issue.created", "issue.resolved"],
  form: ["form.completed"],
  nudge: ["nudge.sent"],
};

type FilterOption = { label: string; value: string };

const SORT_OPTIONS: SortOption[] = [
  { label: "Newest First", value: "occurred_at_desc" },
  { label: "Oldest First", value: "occurred_at_asc" },
];

const DEFAULT_SORT = "occurred_at_desc";

function humanizeEventName(eventName: EventName): string {
  return eventName
    .split(".")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).replace(/_/g, " "))
    .join(" ");
}

function formatFilterDate(value: string | undefined): string | null {
  if (!value) return null;
  try {
    return format(parseISO(value), "MMM d yyyy");
  } catch {
    return value;
  }
}

interface EventFiltersProps {
  filters: PlatformEventFilters;
  onFiltersChange: (filters: PlatformEventFilters) => void;
  onReset: () => void;
}

export default function EventFilters({ filters, onFiltersChange, onReset }: EventFiltersProps) {
  const labelColor = useColorModeValue("gray.600", "gray.300");
  const { data: newHireJourneysData } = useNewHiresListQuery("");
  const newHireJourneys = useMemo(() => (newHireJourneysData ?? []) as NewHireJourney[], [newHireJourneysData]);
  const { data: users = [] } = useUserListQuery("");
  const { data: currentUser } = useCurrentUserQuery();

  // Get available event names for selected event type
  const availableEventNames = useMemo(() => {
    if (!filters.eventType) return [];
    return EVENT_NAMES_BY_TYPE[filters.eventType] || [];
  }, [filters.eventType]);

  // Track active quick filter
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);

  // Quick filter presets - only today, yesterday, and last 7 days
  const applyQuickFilter = (preset: string) => {
    const today = new Date();
    const baseFilters: PlatformEventFilters = {
      ...filters,
      page: 1,
    };

    let newFilters: PlatformEventFilters;
    switch (preset) {
      case "today":
        newFilters = {
          ...baseFilters,
          startDate: format(startOfDay(today), "yyyy-MM-dd"),
          endDate: format(endOfDay(today), "yyyy-MM-dd"),
        };
        break;
      case "yesterday": {
        const yesterday = subDays(today, 1);
        newFilters = {
          ...baseFilters,
          startDate: format(startOfDay(yesterday), "yyyy-MM-dd"),
          endDate: format(endOfDay(yesterday), "yyyy-MM-dd"),
        };
        break;
      }
      case "last7Days":
        newFilters = {
          ...baseFilters,
          startDate: format(startOfDay(subDays(today, 7)), "yyyy-MM-dd"),
          endDate: format(endOfDay(today), "yyyy-MM-dd"),
        };
        break;
      default:
        return;
    }

    setActiveQuickFilter(preset);
    onFiltersChange(newFilters);
  };

  const handleEventTypeChange = (option: FilterOption | FilterOption[] | null) => {
    const singleOption = Array.isArray(option) ? null : option;
    onFiltersChange({
      ...filters,
      eventType: singleOption?.value as EventTypePrefix | undefined,
      eventName: undefined, // Clear specific event name when type changes
      page: 1,
    });
  };

  const handleEventNameChange = (option: FilterOption | FilterOption[] | null) => {
    const singleOption = Array.isArray(option) ? null : option;
    onFiltersChange({
      ...filters,
      eventName: singleOption?.value as EventName | undefined,
      page: 1,
    });
  };

  const handleNewHireChange = (option: FilterOption | FilterOption[] | null) => {
    const singleOption = Array.isArray(option) ? null : option;
    const selectedJourney = newHireJourneys.find((nhj) => nhj.id === singleOption?.value);
    onFiltersChange({
      ...filters,
      newHireJourneyId: singleOption?.value || undefined,
      newHireId: selectedJourney?.newHireId || undefined,
      page: 1,
    });
  };

  const handleActorChange = (option: FilterOption | FilterOption[] | null) => {
    const singleOption = Array.isArray(option) ? null : option;
    onFiltersChange({
      ...filters,
      actorId: singleOption?.value || undefined,
      page: 1,
    });
  };

  const handleStartDateChange = (value: string) => {
    onFiltersChange({
      ...filters,
      startDate: value || undefined,
      page: 1,
    });
  };

  const handleEndDateChange = (value: string) => {
    onFiltersChange({
      ...filters,
      endDate: value || undefined,
      page: 1,
    });
  };

  // Build options
  const eventTypeOptions = useMemo(
    () =>
      EVENT_TYPE_PREFIXES.map((type) => ({
        value: type,
        label: EVENT_TYPE_LABELS[type],
      })),
    [],
  );

  const eventNameOptions = useMemo(
    () =>
      availableEventNames.map((eventName) => ({
        value: eventName,
        label: humanizeEventName(eventName),
      })),
    [availableEventNames],
  );

  const newHireOptions = useMemo(
    () =>
      newHireJourneys.map((journey) => ({
        value: journey.id,
        label:
          journey.newHire?.fullName ||
          `${journey.newHire?.firstName || ""} ${journey.newHire?.lastName || ""}`.trim() ||
          "Unknown",
      })),
    [newHireJourneys],
  );

  const actorOptions = useMemo(() => {
    const options = [
      { value: "system", label: "System" },
      ...users.map((user) => ({
        value: user.id,
        label: user.fullName || `${user.firstName} ${user.lastName}`,
      })),
    ];
    return options;
  }, [users]);

  const currentUserOption = useMemo(() => {
    if (!currentUser) return null;
    const fallbackName = `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim();
    return {
      value: currentUser.id,
      label: currentUser.fullName || fallbackName || "Unknown",
    };
  }, [currentUser]);

  // Derive selected options from filters so selects stay in sync without effects
  const selectedEventType = useMemo(
    () => eventTypeOptions.find((opt) => opt.value === filters.eventType) ?? null,
    [eventTypeOptions, filters.eventType],
  );

  const selectedEventName = useMemo(
    () => eventNameOptions.find((opt) => opt.value === filters.eventName) ?? null,
    [eventNameOptions, filters.eventName],
  );

  const selectedNewHire = useMemo(
    () => newHireOptions.find((opt: FilterOption) => opt.value === filters.newHireJourneyId) ?? null,
    [newHireOptions, filters.newHireJourneyId],
  );

  const selectedActor = useMemo(() => {
    if (!filters.actorId) return null;
    const matching = actorOptions.find((opt) => opt.value === filters.actorId);
    if (matching) return matching;
    return currentUserOption?.value === filters.actorId ? currentUserOption : null;
  }, [actorOptions, currentUserOption, filters.actorId]);

  // Build active filters array
  const hasActiveFilters = !!(
    filters.eventType ||
    filters.eventName ||
    filters.startDate ||
    filters.endDate ||
    filters.newHireJourneyId ||
    filters.actorId
  );

  const activeFilters: ActiveFilter[] = [];

  if (filters.newHireJourneyId) {
    activeFilters.push({
      key: "newHire",
      label: `New Hire: ${selectedNewHire?.label || "Unknown"}`,
      onRemove: () => handleNewHireChange(null),
    });
  }

  if (filters.actorId) {
    activeFilters.push({
      key: "actor",
      label: `Actor: ${selectedActor?.label || "Unknown"}`,
      onRemove: () => handleActorChange(null),
    });
  }

  if (filters.eventType) {
    activeFilters.push({
      key: "eventType",
      label: selectedEventType?.label || "Unknown Event Type",
      onRemove: () => handleEventTypeChange(null),
    });
  }

  if (filters.eventName) {
    activeFilters.push({
      key: "eventName",
      label: selectedEventName?.label || "Unknown Event",
      onRemove: () => handleEventNameChange(null),
    });
  }

  if (filters.startDate || filters.endDate) {
    const startLabel = formatFilterDate(filters.startDate);
    const endLabel = formatFilterDate(filters.endDate);
    let dateLabel: string;
    if (startLabel && endLabel) {
      dateLabel = `${startLabel} to ${endLabel}`;
    } else if (startLabel) {
      dateLabel = `From ${startLabel}`;
    } else {
      dateLabel = `Until ${endLabel || ""}`.trim();
    }
    activeFilters.push({
      key: "dateRange",
      label: dateLabel,
      onRemove: () => {
        onFiltersChange({
          ...filters,
          startDate: undefined,
          endDate: undefined,
          page: 1,
        });
        setActiveQuickFilter(null);
      },
    });
  }

  // Derive sort string from filters
  const currentSort = `${filters.sortBy || "occurred_at"}_${filters.sortOrder || "desc"}`;

  const handleSortChange = (value: string) => {
    const [sortBy, sortOrder] = value.split(/_(?=[^_]+$)/);
    onFiltersChange({
      ...filters,
      sortBy: sortBy,
      sortOrder: sortOrder as PlatformEventFilters["sortOrder"],
      page: 1,
    });
  };

  const handleResetAll = () => {
    setActiveQuickFilter(null);
    onReset();
  };

  return (
    <FilterSortSearchBar
      searchPlaceholder="Search events..."
      onSearchChange={() => {}}
      currentSort={currentSort}
      defaultSort={DEFAULT_SORT}
      onSortChange={handleSortChange}
      sortOptions={SORT_OPTIONS}
      quickFilters={
        <>
          <FilterButton
            onClick={() => {
              if (activeQuickFilter === "today") {
                setActiveQuickFilter(null);
                onFiltersChange({
                  ...filters,
                  startDate: undefined,
                  endDate: undefined,
                  page: 1,
                });
              } else {
                applyQuickFilter("today");
              }
            }}
            isActive={activeQuickFilter === "today"}
            icon={MdToday}
            size="sm"
          >
            Today
          </FilterButton>
          <FilterButton
            onClick={() => {
              if (activeQuickFilter === "yesterday") {
                setActiveQuickFilter(null);
                onFiltersChange({
                  ...filters,
                  startDate: undefined,
                  endDate: undefined,
                  page: 1,
                });
              } else {
                applyQuickFilter("yesterday");
              }
            }}
            isActive={activeQuickFilter === "yesterday"}
            icon={MdCalendarToday}
            size="sm"
          >
            Yesterday
          </FilterButton>
          <FilterButton
            onClick={() => {
              if (activeQuickFilter === "issues") {
                setActiveQuickFilter(null);
                onFiltersChange({
                  ...filters,
                  eventType: undefined,
                  eventName: undefined,
                  page: 1,
                });
              } else {
                setActiveQuickFilter("issues");
                onFiltersChange({
                  ...filters,
                  eventType: "issue",
                  eventName: undefined,
                  page: 1,
                });
              }
            }}
            isActive={activeQuickFilter === "issues"}
            icon={MdBugReport}
            size="sm"
          >
            Issues
          </FilterButton>
        </>
      }
      allFilters={
        <>
          <FilterButton
            onClick={() => {
              if (activeQuickFilter === "today") {
                setActiveQuickFilter(null);
                onFiltersChange({
                  ...filters,
                  startDate: undefined,
                  endDate: undefined,
                  page: 1,
                });
              } else {
                applyQuickFilter("today");
              }
            }}
            isActive={activeQuickFilter === "today"}
            icon={MdToday}
          >
            Today
          </FilterButton>
          <FilterButton
            onClick={() => {
              if (activeQuickFilter === "yesterday") {
                setActiveQuickFilter(null);
                onFiltersChange({
                  ...filters,
                  startDate: undefined,
                  endDate: undefined,
                  page: 1,
                });
              } else {
                applyQuickFilter("yesterday");
              }
            }}
            isActive={activeQuickFilter === "yesterday"}
            icon={MdCalendarToday}
          >
            Yesterday
          </FilterButton>
          <FilterButton
            onClick={() => {
              if (activeQuickFilter === "last7Days") {
                setActiveQuickFilter(null);
                onFiltersChange({
                  ...filters,
                  startDate: undefined,
                  endDate: undefined,
                  page: 1,
                });
              } else {
                applyQuickFilter("last7Days");
              }
            }}
            isActive={activeQuickFilter === "last7Days"}
            icon={MdCalendarToday}
          >
            Last 7 Days
          </FilterButton>
          <FilterButton
            onClick={() => {
              if (activeQuickFilter === "myActions") {
                setActiveQuickFilter(null);
                onFiltersChange({
                  ...filters,
                  actorId: undefined,
                  page: 1,
                });
              } else if (currentUser?.id) {
                setActiveQuickFilter("myActions");
                onFiltersChange({
                  ...filters,
                  actorId: currentUser.id,
                  page: 1,
                });
              }
            }}
            isActive={activeQuickFilter === "myActions"}
            isDisabled={!currentUser?.id}
            icon={MdPerson}
          >
            My Actions
          </FilterButton>
          <FilterButton
            onClick={() => {
              if (activeQuickFilter === "issues") {
                setActiveQuickFilter(null);
                onFiltersChange({
                  ...filters,
                  eventType: undefined,
                  eventName: undefined,
                  page: 1,
                });
              } else {
                setActiveQuickFilter("issues");
                onFiltersChange({
                  ...filters,
                  eventType: "issue",
                  eventName: undefined,
                  page: 1,
                });
              }
            }}
            isActive={activeQuickFilter === "issues"}
            icon={MdBugReport}
          >
            Issues
          </FilterButton>
          <TableFilterSelect
            placeholder="New Hire"
            value={selectedNewHire}
            onChange={handleNewHireChange}
            defaultOptions={newHireOptions}
            minWidth="200px"
            isClearable
          />
          <TableFilterSelect
            placeholder="Actor"
            value={selectedActor}
            onChange={handleActorChange}
            defaultOptions={actorOptions}
            minWidth="180px"
            isClearable
          />
          <TableFilterSelect
            placeholder="Event Type"
            value={selectedEventType}
            onChange={handleEventTypeChange}
            defaultOptions={eventTypeOptions}
            minWidth="200px"
            isClearable
          />
          {filters.eventType && availableEventNames.length > 0 && (
            <TableFilterSelect
              placeholder="Specific Event"
              value={selectedEventName}
              onChange={handleEventNameChange}
              defaultOptions={eventNameOptions}
              minWidth="220px"
              isClearable
            />
          )}
          <Box minW="190px" maxW="190px">
            <VStack spacing={1} align="start">
              <CDS.Text10 color={labelColor}>
                From
              </CDS.Text10>
              <DateFilter value={filters.startDate || ""} onChange={handleStartDateChange} placeholder="Start date" />
            </VStack>
          </Box>
          <Box minW="190px" maxW="190px">
            <VStack spacing={1} align="start">
              <CDS.Text10 color={labelColor}>
                To
              </CDS.Text10>
              <DateFilter value={filters.endDate || ""} onChange={handleEndDateChange} placeholder="End date" />
            </VStack>
          </Box>
        </>
      }
      activeFilters={activeFilters}
      onReset={handleResetAll}
      hasActiveFilters={hasActiveFilters}
    />
  );
}
